/**
 * TextAnswer — free-text answer widget for wizard questions.
 *
 * Used for open-ended questions in the benchmark (background, goals,
 * strengths, weaknesses) and the rare text-type quiz question.
 */

import './TextAnswer.css';

export type TextAnswerProps = {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    rows?: number;
    minLength?: number;
    /** when set, shows a character count with limit */
    maxLength?: number;
    /** renders as a single-line <input> instead of a <textarea> */
    singleLine?: boolean;
    disabled?: boolean;
    /** hint line under the field */
    hint?: string;
};

function TextAnswer({
    value,
    onChange,
    placeholder,
    rows = 5,
    minLength,
    maxLength,
    singleLine,
    disabled,
    hint,
}: TextAnswerProps) {
    const len = value.length;
    const tooShort = minLength !== undefined && len > 0 && len < minLength;
    return (
        <div className="wizard-text">
            {singleLine ? (
                <input
                    type="text"
                    className="wizard-text__input"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={maxLength}
                />
            ) : (
                <textarea
                    className="wizard-text__textarea"
                    rows={rows}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={maxLength}
                />
            )}
            <div className="wizard-text__meta">
                {hint && <span className="wizard-text__hint">{hint}</span>}
                {maxLength !== undefined && (
                    <span className={`wizard-text__count${tooShort ? ' is-short' : ''}`}>
                        {len}
                        {maxLength ? ` / ${maxLength}` : ''}
                    </span>
                )}
            </div>
            {tooShort && (
                <p className="wizard-text__warning">
                    Need at least {minLength} characters for this one.
                </p>
            )}
        </div>
    );
}

export default TextAnswer;
